import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MainNav } from '../components/MainNav';
import {
  EnvelopeIcon,
  CheckCircleIcon,
  CubeIcon,
  ArrowLeftIcon,
  PhoneIcon,
  BuildingOffice2Icon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { FadeIn } from '../components/animations/FadeIn';

const AVAILABLE_PRODUCTS = [
  { slug: 'precept', name: 'PRECEPT', description: 'Waste classification and regulatory tracking' },
  { slug: 'pathfinder', name: 'PATHFINDER', description: 'Technology selection decision engine' },
  { slug: 'polygon', name: 'POLYGON', description: 'Plume containment cost predictor' },
  { slug: 'purge', name: 'PURGE', description: 'Remediation monitoring and optimization' },
  { slug: 'premise', name: 'PREMISE', description: 'Phase I ESA automation' },
  { slug: 'phreatic', name: 'PHREATIC', description: 'Groundwater assessment platform' },
];

const COMPANY_SIZES = [
  { value: 'small', label: '1-50 employees' },
  { value: 'medium', label: '51-200 employees' },
  { value: 'large', label: '201-1000 employees' },
  { value: 'enterprise', label: '1000+ employees' },
];

const HOW_HEARD = [
  { value: 'search', label: 'Web Search' },
  { value: 'referral', label: 'Referral' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'conference', label: 'Conference/Event' },
  { value: 'other', label: 'Other' },
];

export function ContactSales() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    requested_products: [] as string[],
    company_size: '',
    message: '',
    how_heard: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleProductToggle = (slug: string) => {
    setFormData(prev => ({
      ...prev,
      requested_products: prev.requested_products.includes(slug)
        ? prev.requested_products.filter(p => p !== slug)
        : [...prev.requested_products, slug]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.company_name.trim()) {
      setError('Company name is required');
      return;
    }
    if (!formData.contact_name.trim()) {
      setError('Contact name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (formData.requested_products.length === 0) {
      setError('Please select at least one product');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch('/api/v1/admin/inquiries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit inquiry');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black">
        <MainNav />
        <div className="max-w-lg mx-auto px-6 py-24 text-center">
          <FadeIn>
            <div className="inline-flex items-center justify-center p-4 border border-emerald-500/30 bg-emerald-500/10 rounded-full mb-6">
              <CheckCircleIcon className="h-12 w-12 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-widest mb-4">
              INQUIRY SUBMITTED
            </h1>
            <p className="text-sm text-white/60 mb-8">
              Thank you for your interest in ARGOS. Our sales team will contact you within 24-48 hours to discuss your requirements and complete your order.
            </p>
            <div className="space-y-3">
              <Link
                to="/"
                className="block w-full py-3 border border-white/20 text-white text-xs tracking-widest hover:bg-white/5 transition-colors"
              >
                RETURN HOME
              </Link>
            </div>
          </FadeIn>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <MainNav />

      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Back Link */}
        <FadeIn>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs text-white/50 tracking-wider hover:text-white mb-8"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            BACK TO HOME
          </Link>
        </FadeIn>

        {/* Header */}
        <FadeIn delay={0.1}>
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center p-4 border border-emerald-500/30 bg-emerald-500/10 rounded-lg mb-6">
              <EnvelopeIcon className="h-10 w-10 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-widest mb-3">
              CONTACT SALES
            </h1>
            <p className="text-sm text-white/50 tracking-wider max-w-md mx-auto">
              Complete the form below and our team will reach out to finalize your purchase via wire transfer.
            </p>
          </div>
        </FadeIn>

        {/* Form */}
        <FadeIn delay={0.2}>
          <form onSubmit={handleSubmit} className="space-y-8">
            {error && (
              <div className="border border-red-500/30 bg-red-500/10 p-4 text-center">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* Contact Information */}
            <div className="border border-white/10 p-6">
              <h2 className="text-xs text-white/40 tracking-widest mb-6">CONTACT INFORMATION</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] text-white/50 tracking-widest mb-2">
                    <BuildingOffice2Icon className="w-3 h-3 inline mr-1" />
                    COMPANY NAME *
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/20 text-white text-sm focus:border-emerald-500/50 focus:outline-none"
                    placeholder="Acme Environmental Corp"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-white/50 tracking-widest mb-2">
                    <UserIcon className="w-3 h-3 inline mr-1" />
                    CONTACT NAME *
                  </label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/20 text-white text-sm focus:border-emerald-500/50 focus:outline-none"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-white/50 tracking-widest mb-2">
                    <EnvelopeIcon className="w-3 h-3 inline mr-1" />
                    EMAIL ADDRESS *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/20 text-white text-sm focus:border-emerald-500/50 focus:outline-none"
                    placeholder="john@acme.com"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-white/50 tracking-widest mb-2">
                    <PhoneIcon className="w-3 h-3 inline mr-1" />
                    PHONE NUMBER
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/20 text-white text-sm focus:border-emerald-500/50 focus:outline-none"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </div>

            {/* Product Selection */}
            <div className="border border-white/10 p-6">
              <h2 className="text-xs text-white/40 tracking-widest mb-6">SELECT PRODUCTS *</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {AVAILABLE_PRODUCTS.map((product) => (
                  <button
                    key={product.slug}
                    type="button"
                    onClick={() => handleProductToggle(product.slug)}
                    className={`flex items-start gap-3 p-4 border text-left transition-all ${
                      formData.requested_products.includes(product.slug)
                        ? 'border-emerald-500/50 bg-emerald-500/10'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div className={`w-5 h-5 border rounded flex items-center justify-center mt-0.5 ${
                      formData.requested_products.includes(product.slug)
                        ? 'border-emerald-500 bg-emerald-500'
                        : 'border-white/30'
                    }`}>
                      {formData.requested_products.includes(product.slug) && (
                        <CheckCircleIcon className="w-3 h-3 text-black" />
                      )}
                    </div>
                    <div>
                      <div className="text-sm text-white font-medium tracking-wider">{product.name}</div>
                      <div className="text-[10px] text-white/50">{product.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Additional Information */}
            <div className="border border-white/10 p-6">
              <h2 className="text-xs text-white/40 tracking-widest mb-6">ADDITIONAL INFORMATION</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-[10px] text-white/50 tracking-widest mb-2">
                    COMPANY SIZE
                  </label>
                  <select
                    value={formData.company_size}
                    onChange={(e) => setFormData({ ...formData, company_size: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/20 text-white text-sm focus:border-emerald-500/50 focus:outline-none"
                  >
                    <option value="">Select...</option>
                    {COMPANY_SIZES.map(size => (
                      <option key={size.value} value={size.value}>{size.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-white/50 tracking-widest mb-2">
                    HOW DID YOU HEAR ABOUT US?
                  </label>
                  <select
                    value={formData.how_heard}
                    onChange={(e) => setFormData({ ...formData, how_heard: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/20 text-white text-sm focus:border-emerald-500/50 focus:outline-none"
                  >
                    <option value="">Select...</option>
                    {HOW_HEARD.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-white/50 tracking-widest mb-2">
                  MESSAGE / QUESTIONS
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 bg-black border border-white/20 text-white text-sm focus:border-emerald-500/50 focus:outline-none resize-none"
                  placeholder="Tell us about your use case, questions, or special requirements..."
                />
              </div>
            </div>

            {/* Submit */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 bg-emerald-500 text-black text-sm font-medium tracking-widest hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'SUBMITTING...' : 'SUBMIT INQUIRY'}
              </button>
              <p className="text-center text-[10px] text-white/30 tracking-wider mt-4">
                Our sales team will contact you within 24-48 business hours to discuss pricing and complete your order.
              </p>
            </div>
          </form>
        </FadeIn>

        {/* Direct Contact */}
        <FadeIn delay={0.3}>
          <div className="mt-12 border border-white/10 p-6 text-center">
            <p className="text-[10px] text-white/40 tracking-widest mb-3">PREFER TO TALK?</p>
            <p className="text-sm text-white/70">
              Email us directly at{' '}
              <a href="mailto:sales@argostellus.com" className="text-emerald-400 hover:text-emerald-300">
                sales@argostellus.com
              </a>
            </p>
          </div>
        </FadeIn>
      </div>
    </div>
  );
}
